import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import { isBefore, isAfter, set } from 'date-fns';
import PropTypes from 'prop-types';
import { AnimatePresence } from 'framer-motion';
import { Badge, Box, Flex, Link, Text, Stack } from '@chakra-ui/react';
import Div100vh from 'react-div-100vh';
import Button from 'components/Button';
import SplitText from 'components/SplitText';
import { LogoIcon } from 'components/Icons';
import renderModules from 'utils/renderModules';
import { transformThemes } from 'utils/transformThemes';

import '../helvetica.css';

const now = new Date();
const IndexPage = ({
  data: {
    sanityHome: {
      title,
      subtitle,
      _rawSections: sections,
      buttonLabel,
      buttonLink,
    },
    sanityThemes: { _rawThemes },
  },
}) => {
  const today5am = set(now, { hours: 5, minutes: 0, seconds: 0 });
  const today1pm = set(now, { hours: 13, minutes: 0, seconds: 0 });
  const today9pm = set(now, { hours: 21, minutes: 0, seconds: 0 });
  const today24pm = set(now, { hours: 24, minutes: 0, seconds: 0 });

  const [theme, setTheme] = useState(null);

  useEffect(() => {
    if (isAfter(now, today5am) && isBefore(now, today1pm)) {
      // After 5AM - Before 1pm
      setTheme(0);
    } else if (isAfter(now, today1pm) && isBefore(now, today9pm)) {
      // After 1pm - Before 9PM
      setTheme(1);
    } else if (
      (isAfter(now, today9pm) && isBefore(now, today24pm)) ||
      isBefore(now, today5am)
    ) {
      // After 9pm - Before midnight next day
      setTheme(2);
    }
  }, [today5am, today1pm, today9pm, today24pm, theme]);

  const themes = transformThemes(_rawThemes);
  const activeTheme = themes[theme];

  return [0, 1, 2].includes(theme) ? (
    <Box>
      <Box
        zIndex={0}
        background={`
          radial-gradient(circle at top right, ${activeTheme.gradientOne} -5%, ${activeTheme.backgroundTransparent} 10%),
          radial-gradient(circle at -20% 22.5%, ${activeTheme.gradientTwo} -5%, ${activeTheme.backgroundTransparent} 30%),
          radial-gradient(circle at 100% 40%, ${activeTheme.gradientThree} -20%, ${activeTheme.backgroundTransparent} 20%),
          radial-gradient(circle at 0% 70%, ${activeTheme.gradientFour} 0%, ${activeTheme.backgroundTransparent} 40%),
          radial-gradient(circle at 100% 100%, ${activeTheme.gradientFive} 0%, ${activeTheme.backgroundTransparent} 15%)
        `}
        position="absolute"
        width="100%"
        height="100%"
        top={0}
        right={0}
        pointerEvents="none"
      />

      <Box bg={activeTheme.background}>
        <Box
          display={{ base: 'none', lg: 'block' }}
          position="fixed"
          top="1.4vw"
          right="0.85vw"
          zIndex={1}
          as="nav"
          __css={{
            mixBlendMode: 'difference',
          }}
        >
          <Flex direction="column">
            {sections.map((section, i) => (
              <Box
                key={section._key}
                __css={{
                  marginInlineStart: i !== 0 && '1.6vw',
                  writingMode: 'vertical-rl',
                }}
              >
                <Link
                  textTransform="uppercase"
                  fontSize="2.08vw"
                  textDecoration="none"
                  _hover={{
                    textDecoration: 'none',
                  }}
                  href={`#${section.title.toLowerCase()}`}
                >
                  {section.title}
                </Link>
              </Box>
            ))}
          </Flex>
        </Box>
        <Box
          as="main"
          position="relative"
          pl="1.4vw"
          pr={{ base: '1.4vw', lg: '4.86vw' }}
          pb={{ base: '12vw', md: '2vw' }}
          transform="translateZ(0)"
          __css={{
            mixBlendMode: 'difference',
          }}
        >
          <Flex
            mb="25vw"
            as={Div100vh}
            direction="column"
            justify="space-between"
            minHeight="100%"
            pt="1.4vw"
            pb="0.5vw"
          >
            <LogoIcon width={{ base: '25vw', lg: '12vw' }} height="auto" />

            <Text as="h1" textStyle="hero">
              <AnimatePresence>
                <SplitText
                  initial={{
                    y: '100%',
                  }}
                  animate="visible"
                  style={{ originY: 0, originX: 0 }}
                  variants={{
                    visible: (i) => ({
                      y: 0,
                      scale: 1,
                      transition: {
                        duration: 1,
                        ease: [0.16, 1, 0.3, 1],
                        delay: 0.5 + i * 0.025,
                      },
                    }),
                  }}
                >
                  {title}
                </SplitText>
              </AnimatePresence>
            </Text>
          </Flex>

          <Stack spacing="24vw">
            <Text textStyle="body">{subtitle}</Text>
            {sections.map((section) => (
              <Box
                key={section._key}
                pt="1.4vw"
                id={section.title.toLowerCase()}
                outline="none"
              >
                <Badge mb={{ base: '2vw', lg: '1vw' }} as="h2">
                  {section.title}
                </Badge>
                <Stack spacing="10vw">
                  {renderModules(section.content.fields)}
                </Stack>
              </Box>
            ))}
          </Stack>
          <Button as="a" href={buttonLink} mt="4.16vw" width="100%">
            {buttonLabel}
          </Button>
        </Box>
      </Box>
    </Box>
  ) : null;
};

export const query = graphql`
  query HomePageQuery {
    sanityThemes {
      _rawThemes
    }
    sanityHome {
      title
      subtitle
      buttonLabel
      buttonLink
      _rawSections
    }
  }
`;

IndexPage.propTypes = {
  data: PropTypes.shape({
    sanityHome: PropTypes.shape({
      title: PropTypes.string,
      subtitle: PropTypes.string,
      buttonLabel: PropTypes.string,
      buttonLink: PropTypes.string,
      _rawSections: PropTypes.array,
    }),
    sanityThemes: PropTypes.shape({
      _rawThemes: PropTypes.array,
    }),
  }),
};

export default IndexPage;
